import { TyreForce, TyreParams, tyreForce } from './tyre';

/**
 * One wheel's spin over a physics step, with the chassis motion frozen. The tyre force is stiff against the spin
 * (a small change in wheel speed swings the force by thousands of newtons), so each substep is linearly implicit
 * in the spin. Brakes are a friction clamp: they can stop the wheel and then hold it (a locked wheel) as long as the
 * ground does not twist it harder than the brake grips.
 */
export interface SpinInput {
  omega: number;        // rad/s, + rolling forward
  vLong: number;        // m/s of the contact point along the wheel heading
  tanSlipAngle: number;
  muN: number;          // friction * load; 0 in the air
  drive: number;        // N*m from the drivetrain
  brake: number;        // N*m of brake grip available (>= 0)
  inertia: number;      // kg*m^2 as seen by this wheel
  radius: number;
  slipFloor: number;
  tyre: TyreParams;
}

export interface SpinResult { omega: number; fx: number; fy: number; usage: number; slipRatio: number }

const SUBSTEPS = 4;
const T0: TyreForce = { fx: 0, fy: 0, usage: 0, dFxdSlip: 0 };

/** Writes into `out` (reused by the 240 Hz step: no allocation) and returns it. */
export function stepWheelSpin(w: SpinInput, dt: number, out: SpinResult = { omega: 0, fx: 0, fy: 0, usage: 0, slipRatio: 0 }): SpinResult {
  const h = dt / SUBSTEPS;
  const denom = Math.abs(w.vLong) + w.slipFloor;
  const r = w.radius, v = w.vLong;
  let omega = w.omega;
  let fx = 0, fy = 0, usage = 0;
  tyreForce((omega * r - v) / denom, w.tanSlipAngle, w.muN, w.tyre, T0);
  for (let k = 0; k < SUBSTEPS; k++) {
    // d(tyre torque)/d(omega) = R * dFx/dslip * dslip/domega = R^2 dFx/dslip / denom.
    const stiffness = Math.max(0, (T0.dFxdSlip * r * r) / denom);
    const net = w.drive - T0.fx * r; // torque on the wheel before the brake
    const k1 = h / (w.inertia + h * stiffness); // linearly implicit step: omega + k1 * torque
    let next: number;
    if (w.brake <= 0) next = omega + k1 * net;
    else if (omega === 0) next = Math.abs(net) <= w.brake ? 0 : omega + k1 * (net - Math.sign(net) * w.brake);
    else {
      next = omega + k1 * (net - Math.sign(omega) * w.brake);
      if (Math.sign(next) !== Math.sign(omega)) next = 0;
    }
    omega = next;
    tyreForce((omega * r - v) / denom, w.tanSlipAngle, w.muN, w.tyre, T0);
    fx += T0.fx / SUBSTEPS;
    fy += T0.fy / SUBSTEPS;
    usage = Math.max(usage, T0.usage);
  }
  out.omega = omega; out.fx = fx; out.fy = fy; out.usage = usage; out.slipRatio = (omega * r - v) / denom;
  return out;
}
